"use client";

import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import { Notice, StatCard, StatGrid } from "@/components/tool-kit/primitives";
import {
  HINT,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { CpmGantt } from "@/components/tools/accounting/cpm-gantt";
import type { CriticalPathCalculatorStrings } from "@/components/tools/critical-path-calculator";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import type { CpmResult, PertSummary } from "@/lib/tool-kit/accounting/cpm";
import { chartFilename } from "@/lib/tool-kit/charts/layout";

type Strings = CriticalPathCalculatorStrings;

export function CpmResults({
  result,
  pert,
  hasTarget,
  unit,
  strings,
}: {
  result: CpmResult;
  pert: PertSummary | null;
  hasTarget: boolean;
  unit: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const num = (v: number, max = 2) => fmt(v, { maximumFractionDigits: max });
  const [first, ...others] = result.criticalPaths;
  const pathText = (path: string[]) => path.join(" → ");
  const critical = result.activities.filter((a) => a.critical);
  const isPert = pert !== null;
  const head = [
    strings.colId,
    strings.colName,
    strings.colDuration,
    ...(isPert ? [strings.colSigma] : []),
    strings.colPredecessors,
    strings.colEs,
    strings.colEf,
    strings.colLs,
    strings.colLf,
    strings.colSlack,
    strings.colFree,
    strings.colCritical,
  ];
  const sigmaOf = (v: number) => num(Math.sqrt(v), 3);
  const ganttLabel = strings.ganttTitle;
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={isPert ? strings.expectedDuration : strings.projectDuration}
          value={`${num(result.duration)} ${unit}`.trim()}
        />
        <StatCard
          label={strings.criticalCount}
          value={`${fmt(critical.length)} / ${fmt(result.activities.length)}`}
        />
        {pert ? (
          <StatCard label={strings.pathSigma} value={num(pert.sigma, 3)} />
        ) : null}
        {pert ? (
          <StatCard
            label={strings.confidence95}
            value={`${num(pert.confidence95)} ${unit}`.trim()}
          />
        ) : null}
        {pert && hasTarget && pert.probability !== null ? (
          <StatCard
            label={strings.probability}
            value={`${num(pert.probability * 100, 1)}%`}
          />
        ) : null}
      </StatGrid>
      <p className="text-foreground text-sm">
        <span className="font-semibold">{strings.criticalPath}</span>{" "}
        {first ? pathText(first) : "—"}
        {others.length > 0
          ? ` ${tPlural(strings.otherPaths, locale, others.length, { count: fmt(others.length) })}`
          : ""}
        {result.pathsTruncated ? ` ${strings.pathsTruncated}` : ""}
      </p>
      {others.length > 0 ? (
        <ul className="list-disc space-y-1 ps-6 text-foreground text-sm">
          {result.criticalPaths.map((path) => (
            <li key={path.join(">")}>{pathText(path)}</li>
          ))}
        </ul>
      ) : null}
      {pert ? (
        <Notice>
          {t(hasTarget ? strings.pertNoteTarget : strings.pertNote, {
            mean: num(pert.mean),
            sigma: num(pert.sigma, 3),
            z: pert.z === null ? "" : num(pert.z, 3),
            path: pathText(pert.path),
          })}
        </Notice>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              {head.map((cell) => (
                <th className={TH} key={cell} scope="col">
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.activities.map((a) => (
              <tr
                className={a.critical ? "bg-primary/5 font-medium" : ""}
                key={a.id}
              >
                <th className={TD} scope="row">
                  {a.id}
                </th>
                <td className={TD}>{a.name}</td>
                <td className={TD}>{num(a.duration)}</td>
                {isPert ? <td className={TD}>{sigmaOf(a.variance)}</td> : null}
                <td className={TD}>
                  {a.predecessors.length > 0 ? a.predecessors.join(", ") : "—"}
                </td>
                <td className={TD}>{num(a.es)}</td>
                <td className={TD}>{num(a.ef)}</td>
                <td className={TD}>{num(a.ls)}</td>
                <td className={TD}>{num(a.lf)}</td>
                <td className={TD}>{num(a.totalSlack)}</td>
                <td className={TD}>{num(a.freeSlack)}</td>
                <td className={TD}>{a.critical ? strings.yes : strings.no}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={HINT}>{strings.tableNote}</p>
      <h3 className={WIDGET_H3}>{strings.ganttHeading}</h3>
      <ChartFigure
        apa={false}
        data={toTsv([
          [
            strings.colId,
            strings.colName,
            strings.colEs,
            strings.colEf,
            strings.colLf,
            strings.colSlack,
          ],
          ...result.activities.map((a) => [
            a.id,
            a.name,
            a.es,
            a.ef,
            a.lf,
            a.totalSlack,
          ]),
        ])}
        figureNumber="1"
        filename={chartFilename(ganttLabel, "critical-path")}
        labels={strings}
        note=""
        table={null}
        title={ganttLabel}
      >
        <CpmGantt
          activities={result.activities}
          duration={result.duration}
          labels={{
            id: "cpm-gantt",
            label: ganttLabel,
            description: t(strings.ganttDescription, {
              count: fmt(result.activities.length),
              duration: num(result.duration),
              critical: fmt(critical.length),
            }),
            critical: strings.legendCritical,
            normal: strings.legendNormal,
            slack: strings.legendSlack,
          }}
        />
      </ChartFigure>
    </section>
  );
}
